/**
 * Rebuilt heart (wishlist rebuild #22, modal slice #89): state reads go
 * through the new app proxy (<PROXY_URL>/*) using Shopify's signed proxy
 * identity — no Bearer token, no legacy Rails API. Click opens the
 * add-to-wishlist modal (list picker, wishlist-modal.js) for a signed-in
 * customer, legacy parity; the modal owns every write and announces them on
 * the document-level wishlist-item-added / wishlist-item-removed events,
 * which this file consumes for heart state and state-cache coherence.
 *
 * Wire contract (numeric-string ids):
 *   GET <PROXY_URL>/state -> { lists: [{ id, title, itemCount, shared, ownerName, items: [{ id, variantId, productId, quantity }] }] }
 *
 * `shared` lists belong to someone else and are in the read because the
 * customer can edit them (#880). Hearts count them like the customer's own
 * lists, legacy parity (#1016).
 * 401 = logged out (hearts render inactive; click redirects to login).
 *
 * The heart's data-product-id / data-variant-id are the single source of
 * truth for what it acts on. On clothing PDPs, ProductAsVariant rewrites them
 * to the selected size's ids; the attributeChangedCallback below re-derives
 * the active state whenever that happens.
 */

// Seeded by bao-head-wishlist.liquid from the bao_rebuild_proxy_url theme
// setting; the literal covers pages the seed has not run on.
const PROXY_URL = window.BAO?.wishlist?.proxyBase || '/apps/wishlist'

const SIZE_RADIO_SELECTOR = '[data-product-as-variant-el="radio"]'

const MOBILE_SIZE_RADIO_SELECTOR = '[data-product-as-variant-mobile-el="radio"]'

/**
 * One state fetch per page, shared by every heart.
 * @type {Promise<{ loggedIn: boolean, lists: Array<Object> }>|null}
 */
let statePromise = null

/**
 * Hashes whose post-login add has already been claimed. Duplicate hearts for
 * one variant (media + desktop + mobile PDP forms) all match the hash, so the
 * first to claim it is the only one that fires the add.
 * @type {Set<string>}
 */
const claimedHashes = new Set()

/**
 * One size restore per page, shared by every heart.
 * @type {Promise<void>|null}
 */
let sizeRestorePromise = null

/**
 * A clothing PDP returns from login with nothing checked, so the size the
 * interrupted add was for has to be put back before any heart reads its
 * dataset against the hash. Restoring means firing a real radio change event:
 * that is the path the size components listen on, so checked state,
 * aria-current, every heart's dataset ids and the dependent form UI all
 * follow from it. The visible mobile radio is preferred (its handler also
 * syncs the desktop one); otherwise the desktop radio.
 *
 * @returns {Promise<void>}
 */
function restoreSizeFromHash () {
  if (!sizeRestorePromise) {
    sizeRestorePromise = (async () => {
      const match = window.location.hash.match(/^#wishlist-(\d+)$/)

      if (!match) return

      const desktopRadioEl = document.querySelector(
        `${SIZE_RADIO_SELECTOR}[data-variant-id="${match[1]}"]`,
      )
      const mobileRadioEl = document.querySelector(
        `${MOBILE_SIZE_RADIO_SELECTOR}[data-variant-id="${match[1]}"]`,
      )
      const radioEl =
        mobileRadioEl?.offsetParent != null ? mobileRadioEl : desktopRadioEl

      if (!radioEl || radioEl.checked) return

      // The components bind their change handlers in connectedCallback, which
      // runs when define() upgrades the elements already in the document —
      // before whenDefined resolves. Only awaited when the matching radio
      // exists, so pages without the component never wait on a definition
      // that isn't coming.
      if (desktopRadioEl) {
        await window.customElements.whenDefined('product-as-variant')
      }

      if (radioEl === mobileRadioEl) {
        await window.customElements.whenDefined('product-as-variant-mobile')
      }

      radioEl.checked = true

      radioEl.dispatchEvent(new Event('change', { bubbles: true }))
    })()
  }

  return sizeRestorePromise
}

function fetchState () {
  if (!statePromise) {
    statePromise = fetch(`${PROXY_URL}/state`, {
      headers: { Accept: 'application/json' },
    }).then(async response => {
      if (response.status === 401) return { loggedIn: false, lists: [] }

      if (!response.ok) {
        throw new Error(`Wishlist state request failed: ${response.status}`)
      }

      const payload = await response.json()

      return { loggedIn: true, lists: payload.lists }
    })
  }

  return statePromise
}

// Read-only view of the shared cache for the add-to-wishlist modal, which
// paints its rows from it while its fresh /state read is in flight
// (wishlist-modal.js render). Null until the first heart fetches; never
// triggers a fetch itself.
window.BAO.wishlist.cachedState = () => statePromise

/**
 * Upserts an added item into the cached state so a later size switch
 * re-adopts against reality instead of the stale initial GET.
 * If the item's list isn't in the cache (a first-ever add can create one),
 * there isn't enough metadata here to add it honestly, so the cache is
 * dropped for the next read rather than inventing a title/list shape.
 */
async function upsertCachedItem ({ listId, item }) {
  const state = await fetchState()

  const list = state.lists.find(l => String(l.id) === String(listId))

  if (!list) {
    statePromise = null
    return
  }

  const existing = list.items.find(i => String(i.id) === String(item.id))

  if (existing) {
    Object.assign(existing, item)
    return
  }

  // previewImage is the list's first item's image (#327): server-side, an
  // add to a previously-empty list makes this item the new first item, but
  // the event carries no image data to patch it in honestly — drop the
  // cache for the next read rather than leaving the placeholder stale.
  if (list.items.length === 0) {
    statePromise = null
    return
  }

  list.items.push(item)
  list.itemCount = (list.itemCount || 0) + 1
}

/**
 * Removes a POST /items/remove target from the cached state, mirroring
 * upsertCachedItem so the cache stays coherent after a remove too.
 */
async function removeCachedItem ({ listId, itemId }) {
  const state = await fetchState()

  const list = state.lists.find(l => String(l.id) === String(listId))

  if (!list) return

  const index = list.items.findIndex(i => String(i.id) === String(itemId))

  if (index === -1) return

  // previewImage tracks items[0] (#327): removing the current first item
  // changes it (to the new first item's image, or null if now empty), and
  // this event carries no image data for either outcome — drop the cache
  // rather than leaving a removed item's image showing.
  if (index === 0) {
    statePromise = null
    return
  }

  list.items.splice(index, 1)
  list.itemCount = Math.max(0, (list.itemCount || 0) - 1)
}

// The modal owns the writes (#89); these keep the shared state cache
// coherent with what it announces. Bound once at module level — per-heart
// handlers below only manage their own instance state.
document.addEventListener('wishlist-item-added', event => {
  const { listId, itemId, variantId, productId, item } = event.detail

  upsertCachedItem({
    listId,
    item: item ?? { id: itemId, variantId, productId, quantity: 1 },
  })
})

document.addEventListener('wishlist-item-removed', event => {
  removeCachedItem({
    listId: event.detail.listId,
    itemId: event.detail.itemId,
  })
})

class WishlistHeart extends window.BAO.CustomElement({
  SuperClass: HTMLButtonElement,
}) {
  static get observedAttributes () {
    return ['data-variant-id']
  }

  async connectedCallback () {
    super.connectedCallback()

    this.locked = false

    // Ids of the item backing the active state, set once found/created
    this.listId = null
    this.itemId = null

    this.setInitialised()

    // The size restore has to land before the state is read against it, since
    // it moves this heart's dataset ids to the size the add was for
    const [state] = await Promise.all([fetchState(), restoreSizeFromHash()])

    this.adoptFromState(state)

    if (this.hasBeenAdded) {
      this.markActive()
      return
    }

    // Post-login return: the login redirect carries the heart's anchor so the
    // interrupted click resumes on arrival — the modal opens for the product
    // the customer was adding, matching what a click does everywhere (#89)
    const hash = `#wishlist-${this.variantId}`

    if (window.location.hash !== hash) return

    // Claimed synchronously so only one of the duplicate hearts fires
    if (claimedHashes.has(hash)) return

    claimedHashes.add(hash)

    // Called rather than click()ed: a duplicate heart can be hidden or
    // disabled (sold-out forms), and the resume must not hang on that.
    // onHeartClicked keeps its own no-selection and lock guards either way.
    this.onHeartClicked(new Event('click'))
  }

  /**
   * ProductAsVariant pointed this heart at a different variant, so the ids
   * adopted for the previous one are stale and the active state has to be
   * re-derived. oldValue is null during upgrade, when connectedCallback
   * handles the initial adoption.
   */
  attributeChangedCallback (name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue) return

    this.onVariantChanged()
  }

  setupListeners () {
    super.setupListeners()

    this.onHeartClicked = this.onHeartClicked.bind(this)
    this.onItemAdded = this.onItemAdded.bind(this)
    this.onItemRemoved = this.onItemRemoved.bind(this)

    this.listeners.add(this, 'click', this.onHeartClicked)

    if (this.parentTrigger) {
      this.listeners.add(this.parentTrigger, 'click', this.onHeartClicked)
    }

    // Duplicate hearts for the same variant (desktop + mobile PDP forms,
    // product cards) stay in sync through these document-level events
    this.listeners.add(document, 'wishlist-item-added', this.onItemAdded)
    this.listeners.add(document, 'wishlist-item-removed', this.onItemRemoved)
  }

  async onHeartClicked (event) {
    event.preventDefault()

    if (this.locked) return

    // Early return when this button is for clothing sizes (on pdp) and no
    // sizes are selected: its dataset still holds the parent product's ids
    if (this.isClothingHeart) {
      const radioEls = document.querySelectorAll(SIZE_RADIO_SELECTOR)

      if (
        radioEls.length &&
        !Array.from(radioEls).some(radioEl => radioEl.checked)
      ) {
        return
      }
    }

    this.locked = true

    try {
      const state = await fetchState()

      if (!state.loggedIn) {
        window.BAO.wishlist.redirectToLogin(`wishlist-${this.variantId}`)
        return
      }

      // Multi-variant heart (#90): the variant has to be chosen before the
      // add, so route through the variant-selector modal instead
      if (this.hasAttribute('site-modal-id')) {
        this.openVariantSelector()
        return
      }

      // The variant-selector CTA carries empty ids until a variant is
      // chosen; with nothing chosen there is nothing to add
      if (!this.variantId) return

      this.openModal()
    } catch (error) {
      this.log('wishlist-heart click failed', error)
    } finally {
      this.locked = false
    }
  }

  /**
   * Point the add-to-wishlist modal at this heart's product and open it —
   * the legacy heart's behaviour, restored by #89. The modal element is the
   * globally rendered wishlist-modal section, resolved through
   * data-wishlist-heart-external-el="modal" on its root.
   */
  openModal () {
    const modal = this.externalEls?.modal?.element

    if (!modal) {
      this.log('wishlist-heart: add-to-wishlist modal not in the document')
      return
    }

    modal.dataset.productId = this.productId ?? ''
    modal.dataset.variantId = this.variantId ?? ''

    modal.open()
  }

  /**
   * Open the global variant-selector modal (the legacy clothing-size picker,
   * generalised by #90). site-modal.js populates it from this heart's
   * data-variant-info block, and its CTA, itself a wishlist-heart, opens
   * the add modal with the chosen product/variant ids.
   */
  openVariantSelector () {
    this.dispatchEvent(
      new CustomEvent('site-modal:open', {
        bubbles: true,
        detail: { id: this.getAttribute('site-modal-id') },
      }),
    )
  }

  onItemAdded (event) {
    if (!this.matchesVariant(event.detail.variantId)) return

    this.listId = event.detail.listId
    this.itemId = event.detail.itemId

    this.markActive()
  }

  onItemRemoved (event) {
    if (!this.matchesVariant(event.detail.variantId)) return

    this.listId = null
    this.itemId = null

    this.markInactive()
  }

  async onVariantChanged () {
    // Dropped before the await, not after: they belong to the variant the
    // heart has just left, and a remove firing in the gap must not send them
    this.listId = null
    this.itemId = null

    const state = await fetchState()

    this.adoptFromState(state)

    if (this.hasBeenAdded) {
      this.markActive()
    } else {
      this.markInactive()
    }
  }

  /**
   * Find this heart's variant in the fetched lists and adopt its ids.
   * Matches the primary variant id or any id in data-variant-ids (sets).
   */
  adoptFromState (state) {
    for (const list of state.lists) {
      const item = list.items.find(i => this.matchesVariant(i.variantId))

      if (item) {
        this.listId = list.id
        this.itemId = item.id
        return
      }
    }
  }

  matchesVariant (variantId) {
    if (variantId == null) return false

    if (String(variantId) === String(this.variantId)) return true

    return Boolean(
      this.dataset?.variantIds?.split(',')?.includes(String(variantId)),
    )
  }

  markActive () {
    this.classList.add('wlt-Heart-active')
    this.setAttribute('aria-pressed', 'true')
  }

  markInactive () {
    this.classList.remove('wlt-Heart-active')
    this.setAttribute('aria-pressed', 'false')
  }

  setInitialised () {
    this.setAttribute('data-initialised', 'true')
  }

  get active () {
    return this.classList.contains('wlt-Heart-active')
  }

  get hasBeenAdded () {
    return Boolean(this.listId && this.itemId)
  }

  get isClothingHeart () {
    return Boolean(this.closest('[data-clothing-sizes-wishlist-button]'))
  }

  get variantId () {
    return this.dataset?.variantId
  }

  get productId () {
    return this.dataset?.productId
  }

  get parentTrigger () {
    return this.closest('[data-wishlist-heart-parent-trigger]')
  }
}

customElements.define('wishlist-heart', WishlistHeart, {
  extends: 'button',
})
